import * as React from 'react'

import apis from '@app/api/api'
import useI18n from '@store/i18n/useI18n'
import Logger from '@utils/Logger'
import theme from '@theme/Theme'
import { motion, container } from '@utils/Motion'
import rights from '@utils/RightManager'

import styled from '@theme/styled-components'
import Searchbar from '@components/searchbar/searchbar'
import Button from '@components/button/button'
import Loader from 'react-loader-spinner'
import TextInput from '@components/textInput/textInput'
import Select from '@components/select/select'
import MultiSelect from '@components/select/multiSelect'

import * as JsSearch from 'js-search'
import AccentSanitizer from '../../../utils/AccentSanitizer'
import Swal from 'sweetalert2'
import ScreenContainer from '@components/screenContainer/screenContainer'
import InfiniteList from '@components/infiniteList/infiniteList'
import InternalUser from '@components/listItem/internalUser'

const INITIAL_ROLES = ['super_admin', 'intern_user', 'intern_admin']
const NEW_USER: AdminInternalUser = {
  id: '',
  firstname: '',
  lastname: '',
  email: '',
  idGrdf: undefined,
  role: '',
  regions: [],
}
interface Props {
  navigation: Navigation
}

const sanitizer = new AccentSanitizer()
const searchjs = new JsSearch.Search('id')

searchjs.searchIndex = new JsSearch.UnorderedSearchIndex()
searchjs.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
searchjs.tokenizer = {
  tokenize: (text: string) => text.split(' '),
}
searchjs.sanitizer = sanitizer
searchjs.addIndex('firstname')
searchjs.addIndex('lastname')
searchjs.addIndex('email')

const adminInternals = ({ navigation }: Props) => {
  const i18n = useI18n()

  const [screenStatus, setScreenStatus] = React.useState<'fetched' | 'loading' | 'error'>('loading')
  const [text, setText] = React.useState<string>('')
  const [adminUsers, setAdminUsers] = React.useState<AdminInternalUser[]>([])
  const [sortedUsers, setSortedUsers] = React.useState<AdminInternalUser[]>([])
  const [selectedUser, setSelectedUser] = React.useState<AdminInternalUser | undefined>(undefined)
  const [regionList, setRegionList] = React.useState<Option[]>([])
  const [dirtyForm, setDirtyForm] = React.useState<boolean>(false)
  const [formType, setFormType] = React.useState<undefined | 'editing' | 'adding'>(undefined)

  React.useEffect(() => {
    fetchUsers()
  }, [])

  React.useEffect(() => {
    searchjs.addDocuments(adminUsers)
    const filteredUsers = filterUser()
    const sortedUsers = sortUser(filteredUsers)
    setSortedUsers(sortedUsers)
  }, [adminUsers, text])

  const filterUser = (): AdminInternalUser[] =>
    text && searchjs ? (searchjs.search(sanitizer.sanitize(text.trim())) as AdminInternalUser[]) : adminUsers

  const sortUser = (users: AdminInternalUser[]): AdminInternalUser[] =>
    users.sort((a, b) => a.firstname.localeCompare(b.firstname))

  const fetchUsers = () =>
    Promise.all([apis.admin.internal.list(), apis.admin.list.regions()])
      .then(([fetchedUsers, fetchedRegions]) => {
        setAdminUsers(fetchedUsers.internals)
        setRegionList(fetchedRegions.regions.map((r) => ({ label: r.libelle, value: r.id })))
        searchjs.addDocuments(fetchedUsers.internals)
        setScreenStatus('fetched')
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING ADMIN USERS : ', err)
        setScreenStatus('error')
      })

  const setFormField = (callback: () => void) => {
    setDirtyForm(true)
    callback()
  }

  const alertCancellingEdition = (callback?: () => void) => {
    if (dirtyForm) {
      Swal.fire({
        title: i18n.t('components.adminInternals.alertCancelling.title'),
        text: i18n.t('components.adminInternals.alertCancelling.text'),
        showCancelButton: true,
        confirmButtonText: i18n.t('components.adminInternals.alertCancelling.confirm'),
        confirmButtonColor: 'green',
        cancelButtonText: i18n.t('components.adminInternals.alertCancelling.cancel'),
        cancelButtonColor: 'red',
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedUser(undefined)
          setFormType(undefined)
          setDirtyForm(false)
          !!callback && callback()
        }
      })
    } else {
      setSelectedUser(undefined)
      setFormType(undefined)
      setDirtyForm(false)
      !!callback && callback()
    }
  }

  const saveUser = (type: 'editing' | 'adding', user: AdminInternalUser) => {
    ;(type === 'adding' ? apis.admin.internal.add(user) : apis.admin.internal.edit(user))
      .then((u: AdminInternalUser) => {
        Swal.fire({
          title: i18n.t(`components.adminInternals.${type === 'adding' ? 'alertAdding' : 'alertSaving'}.success.title`),
          text: i18n.t(`components.adminInternals.${type === 'adding' ? 'alertAdding' : 'alertSaving'}.success.text`),
          icon: 'success',
        })
        const copyAdminUsers = [...adminUsers]
        setAdminUsers(
          type === 'adding' ? [...copyAdminUsers, u] : [...copyAdminUsers.filter((us) => us.id !== u.id), u]
        )
        setSelectedUser(undefined)
        setFormType(undefined)
        setDirtyForm(false)
      })
      .catch((err) => {
        Swal.fire({
          title: i18n.t(`components.adminInternals.${type === 'adding' ? 'alertAdding' : 'alertSaving'}.failure.title`),
          text: i18n.t(`components.adminInternals.${type === 'adding' ? 'alertAdding' : 'alertSaving'}.failure.text`),
          icon: 'error',
        })
        Logger.info('ERROR ' + (type === 'editing' ? 'SAVING' : 'ADDING') + ' USER : ', err)
      })
  }

  const valideUser = React.useMemo(() => {
    return (
      !!selectedUser &&
      !!selectedUser.firstname &&
      !!selectedUser.lastname &&
      !!selectedUser.email &&
      !!selectedUser.email.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) &&
      (!selectedUser.idGrdf || selectedUser.idGrdf.match(/^.{6}$/)) &&
      !!selectedUser.role &&
      selectedUser.regions.length > 0
    )
  }, [selectedUser])

  const formErrors = React.useMemo(
    () =>
      selectedUser && {
        firstname: !selectedUser.firstname ? i18n.t('components.adminInternals.errors.firstname') : undefined,
        lastname: !selectedUser.lastname ? i18n.t('components.adminInternals.errors.lastname') : undefined,
        email: !selectedUser.email
          ? i18n.t('components.adminInternals.errors.email.noEmail')
          : !selectedUser.email.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)
          ? i18n.t('components.adminInternals.errors.email.wrongEmail')
          : undefined,
        idGrdf:
          !!selectedUser.idGrdf && !selectedUser.idGrdf.match(/^.{6}$/)
            ? i18n.t('components.adminInternals.errors.idGrdf')
            : undefined,
        role: !selectedUser.role ? i18n.t('components.adminInternals.errors.role') : undefined,
        regions: selectedUser.regions.length === 0 ? i18n.t('components.adminInternals.errors.regions') : undefined,
      },
    [selectedUser]
  )

  const deleteUser = () => {
    if (!!selectedUser) {
      Swal.fire({
        title: i18n.t('components.adminInternals.alertDeleting.confirmation.title'),
        text: i18n.t('components.adminInternals.alertDeleting.confirmation.text'),
        showCancelButton: true,
        confirmButtonText: i18n.t('components.adminCompanies.alertCancelling.confirm'),
        confirmButtonColor: 'green',
        cancelButtonText: i18n.t('components.adminCompanies.alertCancelling.cancel'),
        cancelButtonColor: 'red',
      })
        .then((result) => {
          if (result.isConfirmed) {
            Swal.fire({
              didOpen: () => {
                Swal.showLoading()
              },
            })
            apis.admin.internal
              .delete(selectedUser.id)
              .then(() => {
                Swal.fire({
                  title: i18n.t(`components.adminInternals.alertDeleting.success.title`),
                  text: i18n.t(`components.adminInternals.alertDeleting.success.text`),
                  icon: 'success',
                })
                setAdminUsers(adminUsers.filter((user) => user.id !== selectedUser.id))
                setSelectedUser(undefined)
                setFormType(undefined)
                setDirtyForm(false)
              })
              .catch((err) => {
                if (err.code === 400) {
                  Swal.fire({
                    title: i18n.t(`components.adminInternals.alertDeleting.failure.400.title`),
                    text: i18n.t(`components.adminInternals.alertDeleting.failure.400.text`),
                    icon: 'error',
                  })
                } else {
                  Swal.fire({
                    title: i18n.t(`components.adminInternals.alertDeleting.failure.title`),
                    text: i18n.t(`components.adminInternals.alertDeleting.failure.text`),
                    icon: 'error',
                  })
                }
                Logger.error('ERROR DELETE INTERNAL USER : ', err)
              })
          }
        })
        .catch((err) => {
          Logger.info('ERROR POPUP DELETE INTERNAL USER', err)
        })
    }
  }

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.adminInternals.screenTitle')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={theme.colors.classicButtonLoader} height={30} />
            </StatusContainer>
          ) : screenStatus === 'error' ? (
            <ErrorLibelle>{i18n.t('components.adminInternals.error')}</ErrorLibelle>
          ) : (
            <>
              <Searchbar placeholder={i18n.t('components.adminInternals.searchbar')} text={text} setText={setText} />
              <UserList as={motion.div} className="container" variants={container} initial="hidden" animate="visible">
                {sortedUsers.length === 0 ? (
                  <NoItemContainer>
                    <NoItemLibelle>{i18n.t('components.adminInternals.noItem')}</NoItemLibelle>
                  </NoItemContainer>
                ) : (
                  <InfiniteList
                    data={sortedUsers}
                    height={100}
                    renderItem={(u) => (
                      <InternalUser
                        user={u}
                        onClick={() =>
                          alertCancellingEdition(() => {
                            setSelectedUser(u)
                            setFormType('editing')
                          })
                        }
                        height={100}
                      />
                    )}
                  />
                )}
              </UserList>
              {rights.canCreateUsers() && (
                <ButtonContainer>
                  <Button
                    title={i18n.t('components.adminInternals.addItem')}
                    status="active"
                    type="navigation"
                    size="medium"
                    onClick={() =>
                      alertCancellingEdition(() => {
                        setSelectedUser(NEW_USER)
                        setFormType('adding')
                      })
                    }
                    fitContent
                    icon="add"
                  />
                </ButtonContainer>
              )}
            </>
          )}
        </LeftContainer>
      }
      content={
        <RightContainer>
          {!!selectedUser ? (
            <FormContainer>
              <SectionTitleContainer>{i18n.t('components.adminInternals.section01')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminInternals.item01')}
                      value={selectedUser.firstname}
                      onChange={(firstname) => setFormField(() => setSelectedUser({ ...selectedUser, firstname }))}
                      placeholder={i18n.t('components.adminInternals.placeholder01')}
                      error={formErrors && formErrors.firstname}
                    />
                  </FormItemContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminInternals.item03')}
                      value={selectedUser.lastname}
                      onChange={(lastname) => setFormField(() => setSelectedUser({ ...selectedUser, lastname }))}
                      placeholder={i18n.t('components.adminInternals.placeholder03')}
                      error={formErrors && formErrors.lastname}
                    />
                  </FormItemContainer>
                </RowContainer>
                <RowContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminInternals.item02')}
                      value={selectedUser.email}
                      onChange={(email) => setFormField(() => setSelectedUser({ ...selectedUser, email }))}
                      placeholder={i18n.t('components.adminInternals.placeholder02')}
                      error={formErrors && formErrors.email}
                      nonEditable={formType === 'editing'}
                    />
                  </FormItemContainer>
                </RowContainer>
                <RowContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminInternals.item07')}
                      value={selectedUser.idGrdf || ''}
                      onChange={(idGrdf) =>
                        setFormField(() => setSelectedUser({ ...selectedUser, idGrdf: idGrdf.toUpperCase() }))
                      }
                      placeholder={i18n.t('components.adminInternals.placeholder07')}
                      error={formErrors && formErrors.idGrdf}
                    />
                  </FormItemContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminInternals.item04')}
                      value={i18n.t('components.adminInternals.defaultCompany')}
                      onChange={() => undefined}
                      placeholder={i18n.t('components.adminInternals.placeholder04')}
                      nonEditable
                    />
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>

              <SectionTitleContainer>{i18n.t('components.adminInternals.section02')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <SelectorContainer>
                      <SelectorTitle>{i18n.t('components.adminInternals.item05')}</SelectorTitle>
                      <Select
                        options={INITIAL_ROLES.map((r) => ({ label: i18n.t(`userRoles.${r}`), value: r }))}
                        selected={
                          selectedUser.role
                            ? { label: i18n.t(`userRoles.${selectedUser.role}`), value: selectedUser.role }
                            : undefined
                        }
                        setSelected={(selected) =>
                          setFormField(() => setSelectedUser({ ...selectedUser, role: selected ? selected.value : '' }))
                        }
                        isClearable
                        placeholder={i18n.t('components.adminInternals.placeholder05')}
                        error={formErrors && formErrors.role}
                        noMargin
                      />
                    </SelectorContainer>
                  </FormItemContainer>
                  <FormItemContainer>
                    <SelectorContainer>
                      <SelectorTitle>{i18n.t('components.adminInternals.item06')}</SelectorTitle>
                      <MultiSelect
                        options={regionList}
                        selected={selectedUser.regions}
                        setSelected={(selected) =>
                          setFormField(() => setSelectedUser({ ...selectedUser, regions: selected || [] }))
                        }
                        placeholder={i18n.t('components.adminInternals.placeholder06')}
                        error={formErrors && formErrors.regions}
                        noMargin
                      />
                    </SelectorContainer>
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>
              <FormButtonContainer adding={formType === 'adding'}>
                {formType !== 'adding' && (
                  <Button
                    title="Supprimer"
                    status="active"
                    type="classic"
                    size="medium"
                    onClick={deleteUser}
                    icon="cancel"
                  />
                )}
                <Button
                  title={i18n.t('components.adminInternals.cancel')}
                  status={dirtyForm ? 'active' : 'disabled'}
                  type="classic"
                  size="medium"
                  onClick={() => alertCancellingEdition()}
                  icon="bin"
                />
                <Button
                  title={
                    formType === 'adding'
                      ? i18n.t('components.adminInternals.addSaveItem')
                      : i18n.t('components.adminInternals.saveItem')
                  }
                  status={dirtyForm && selectedUser && formType && valideUser ? 'active' : 'disabled'}
                  type="navigation"
                  size="medium"
                  onClick={() => selectedUser && formType && saveUser(formType, selectedUser)}
                  icon="add"
                />
              </FormButtonContainer>
            </FormContainer>
          ) : (
            <NoSelectedContainer>
              <NoSelectedLibelle>{i18n.t('components.adminInternals.noSelected')}</NoSelectedLibelle>
            </NoSelectedContainer>
          )}
        </RightContainer>
      }
    />
  )
}

export default adminInternals

const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const UserList = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: scroll;
  padding-bottom: 75px;
`

const NoItemContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoItemLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const ButtonContainer = styled('div')`
  flex: 1;
  display: flex;
  position: fixed;
  align-items: center;
  justify-content: center;
  bottom: 0;
  height: 75px;
  width: 440px;
  background: linear-gradient(359.87deg, #ffffff 10%, rgba(255, 255, 255, 0) 106.99%);
`
const RightContainer = styled('div')`
  flex: 1;
  display: flex;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const FormContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 50px;
  justify-content: flex-start;
  overflow: scroll;
`
const NoSelectedContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoSelectedLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const SectionTitleContainer = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 20px;
  line-height: 24px;
  color: #0053a1;
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  margin: 10px;
  margin-top: 0;
`
const ColumnContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')`
  display: flex;
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const FormButtonContainer = styled('div')<{ adding: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-around;
  position: absolute;
  bottom: 25px;
  right: 25px;
  min-width: ${(props) => (props.adding ? '600px' : '900px')};
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 14px;
  line-height: 19px;
  color: #9494a2;
`
